import './styles/LoginStyle.css';
import '../base-styles/index.css';
import { useEffect, useState } from 'react';
import { ControlledNumericInput, NavigationButton } from '../common';
import Spinner from 'react-bootstrap/Spinner';
import { ReactComponent as DHLLogo } from '../../resources/images/dhl_logo.svg';
import { useActions } from '../../hooks/useActions';
import { useTypedSelector } from '../../hooks';
import { languageMap } from '../../maps';
import { indexedDBInstance } from '../../indexedDB';

const Login: React.FC = () => {
  const language = useTypedSelector(({ session }) => {
    return session.language;
  });
  const token = useTypedSelector(({ session }) => {
    return session.dhlApiToken;
  });
  const needsSessionOpen = useTypedSelector(({ session }) => {
    return session.needsSessionOpen;
  });
  const needsLogin = useTypedSelector(({ session }) => {
    return session.needsLogin;
  });
  const authenticating = useTypedSelector(({ session }) => {
    return session.authenticating;
  });
  const authErrorMessage = useTypedSelector(({ session }) => {
    return session.authErrorMessage;
  });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [installationId, setInstallationId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const { sessionAuthentication, sessionNeedsSessionOpen } = useActions();
  useEffect(() => {
    indexedDBInstance.clear();
  }, []);
  useEffect(() => {
    console.log('needsLogin: ', needsLogin);
    setLoading(false);
  }, [needsSessionOpen, needsLogin]);
  useEffect(() => {
    console.log('authenticating: ', authenticating);
    if (!authenticating) {
      setLoading(false);
    }
  }, [authenticating]);
  useEffect(() => {
    console.log('authErrorMessage: ', authErrorMessage);
    if (authErrorMessage !== '') {
      setLoading(false);
    }
  }, [authErrorMessage]);
  const requestLogin = () => {
    if (username === '' || password === '') {
      setErrorMsg('Please provide credentials');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    sessionAuthentication(username, password);
  };
  const requestOpenSession = () => {
    if (installationId === '') {
      setErrorMsg('Please provide installation ID');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    sessionAuthentication(username, password, installationId, token);
  };
  const handleKeypress = (e: any) => {
    //it triggers by pressing the enter key
    if (e.key === 'Enter') {
      if (!needsSessionOpen) {
        requestLogin();
      } else {
        requestOpenSession();
      }
    }
  };
  const renderSpinner = () => {
    if (loading) {
      return (
        <div className="spinner-container center-self">
          <Spinner className="spinner" animation="grow" variant="danger" />
        </div>
      );
    }
    return null;
  };
  const renderInstructions = () => {
    if (!needsSessionOpen) {
      return languageMap.login.instructions_login[language];
    }
    return languageMap.login.instructions_install_id[language];
  };
  const renderContent = () => {
    if (loading) {
      return null;
    }
    return (
      <div className="login-content-container-a">
        <div className="login-content-container-b">
          <div className="login-text-container">
            <p className="header centered">{renderInstructions()}</p>
          </div>
          <form className="login-form-container">
            <div>
              <p className="nomargin login-env-title">
                {languageMap.login.env_title[language]}
              </p>
              <p className="nomargin login-env">
                {process.env.REACT_APP_API_ENV}
              </p>
            </div>
            <input
              data-cy="username"
              name="username"
              autoComplete="off"
              className="main-text main-input"
              placeholder={languageMap.login.user_placeholder[language]}
              value={username}
              onChange={(e) => {
                setUsername(e.target.value.toString());
                setErrorMsg('');
                sessionNeedsSessionOpen(false);
              }}
              onKeyDown={handleKeypress}
            />
            <input
              data-cy="password"
              name="password"
              autoComplete="off"
              className="main-text main-input"
              placeholder={languageMap.login.password_placeholder[language]}
              type="password"
              value={password}
              disabled={needsSessionOpen}
              onChange={(e) => {
                setPassword(e.target.value.toString());
                setErrorMsg('');
                sessionNeedsSessionOpen(false);
              }}
              onKeyDown={handleKeypress}
            />
            <div className="install-id-container">
              {renderInstallationInput()}
            </div>
          </form>
        </div>
      </div>
    );
  };
  const renderInstallationInput = () => {
    if (needsSessionOpen) {
      return (
        <ControlledNumericInput
          val={installationId !== null ? installationId : ''}
          inputLabel={languageMap.login.installation_id_placeholder[language]}
          inputPlaceholder="#"
          validationError={false}
          maxLength={10}
          maxNumber={100000}
          type="integer"
          onError={(isErr: boolean) => {
            if (isErr) {
              let msg = languageMap.login.installation_id_error[language];
              setErrorMsg(msg);
            } else {
              setErrorMsg('');
            }
          }}
          onTextChange={(newVal: string) => {
            setErrorMsg('');
            setInstallationId(newVal);
          }}
          handleKeypress={handleKeypress}
          dataCy="installation-id"
          extraClassContainer="no-padding-right full-width"
          extraClass="placeholder-left"
        />
      );
    }
  };
  const getErrorMessageVisibleState = () => {
    if (authErrorMessage !== '' || errorMsg !== '') {
      return 'visible';
    }
    return '';
  };
  const getErrorMessage = () => {
    if (authErrorMessage !== '') {
      return authErrorMessage;
    }
    if (errorMsg !== '') {
      return errorMsg;
    }
    return '';
  };
  const renderLoginButton = () => {
    if (!loading) {
      return (
        <div className="login-button-container">
          <p
            className={`button-feedback nomargin error ${getErrorMessageVisibleState()}`}
          >
            {getErrorMessage()}
          </p>
          <NavigationButton
            dataCy="login-button"
            buttonContent={languageMap.login.login_buttton[language]}
            buttonClass="center-button confirm-button login-button"
            onClickButton={() => {
              if (!needsSessionOpen) {
                requestLogin();
              } else {
                requestOpenSession();
              }
            }}
          />
        </div>
      );
    }
  };
  return (
    <div id="login" className="main-container">
      <div className="center-banner" />
      <DHLLogo className="dhl-logo centered" />
      {renderSpinner()}
      {renderContent()}
      {renderLoginButton()}
    </div>
  );
};

export { Login };
